import React, { useEffect, useState } from 'react';
import { BubbleButton, BubbleFormGroup, BubbleInput } from '../BubbleUI';
import { Brand, BrandCommonText } from '@go-shared/types/brand';
import { updateBrand } from '@go-client/lib/api/brand';
import { createPortal } from 'react-dom';

interface BrandCommonTextsFormDataBase {
  commonTexts: BrandCommonText[];
}

type BrandCommonTextsFormData = BrandCommonTextsFormDataBase & {
  _dirty: boolean;
  _errors: Partial<BrandFormError>;
};

type BrandFormError = { [K in keyof BrandCommonTextsFormDataBase]: string };

function DEFAULT_FORM_DATA(brand?: Brand): BrandCommonTextsFormData {
  return {
    commonTexts: brand?.commonTexts ?? [],
    _dirty: false,
    _errors: {},
  };
}

interface BrandFormProps {
  edit: Brand;
  onClose?: () => void;
  actionsElem?: HTMLDivElement;
  onFormSubmitted: () => Promise<void>;
}

export default function BrandCommonTextsForm(props: BrandFormProps) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<BrandCommonTextsFormData>(DEFAULT_FORM_DATA(props.edit));

  useEffect(() => {
    resetForm();
  }, [props.edit]);

  function resetForm() {
    setFormData(DEFAULT_FORM_DATA(props.edit));
  }

  function changeForm<T extends keyof BrandCommonTextsFormDataBase>(
    key: T,
    val: BrandCommonTextsFormDataBase[T]
  ) {
    setFormData({ ...formData, [key]: val, _dirty: true });
  }

  async function submitForm() {
    setSaving(true);
    try {
      if (props.edit) {
        // user intends to edit an existing property
        await updateBrand(props.edit._id, {
          ...props.edit,

          commonTexts: formData.commonTexts,
        });
      }

      await props.onFormSubmitted();
      if (props.onClose) props.onClose();
    } finally {
      setSaving(false);
    }
  }

  const errored = Object.keys(formData._errors).length > 0;

  const formHtml = (
    <div className="grid grid-cols-3 gap-4">
      {formData.commonTexts.map(text => (
        <BubbleFormGroup label={text.label} className="col-span-3">
          <BubbleInput
            type="text"
            block
            value={text.name}
            onChange={value => {
              const idx = formData.commonTexts.findIndex(s => s._id === text._id);
              if (idx < 0) return;
              const newElement = [...formData.commonTexts];
              newElement.splice(idx, 1, { ...text, name: value.target.value });
              changeForm('commonTexts', newElement);
            }}
          />
        </BubbleFormGroup>
      ))}
    </div>
  );

  const formActions = (
    <div className="flex items-center">
      <div className="ml-auto" />
      <BubbleButton
        variant="navy"
        onClick={resetForm}
        inverse
        disabled={!formData._dirty || errored || saving}
        icon="rotate-left"
      >
        Reset
      </BubbleButton>
      <BubbleButton
        variant="navy"
        onClick={submitForm}
        disabled={!formData._dirty || errored || saving}
        icon="floppy-disk"
      >
        Save Changes
      </BubbleButton>
    </div>
  );

  const portalButtons = props.actionsElem ? createPortal(formActions, props.actionsElem) : <></>;

  return (
    <>
      {formHtml}
      {portalButtons}
    </>
  );
}
